'use client';

import { faAngleDown, faAngleUp, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';
import { Button, Menu } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { DebounceInput } from 'react-debounce-input';

import { useGetLeagueStatesByCountry } from '@/app/(default)/(leagues)/leagues/queries';
import { FilterState } from '@/modules/league/search-page/Filters';
import { Country } from '@/types/players.types';
import { getLeagueSearchParams } from '@/utils/actions/leagues/gameDay/functions/getLeagueSearchParams';

export const playerGroupData = [
  {
    label: 'Mens',
    value: '1',
  },
  {
    label: 'Womens',
    value: '2',
  },
  {
    label: 'Mixed',
    value: '3',
  },
  {
    label: 'Coed',
    value: '4',
  },
];

export const formatsData = [
  {
    label: 'Doubles',
    value: '1', //DS
  },
  {
    label: 'Singles',
    value: '2',
  },
  {
    label: 'Skinny Singles',
    value: '6',
  },
];

export type Weekday = {
  label: string;
  parameter: 'Day_Su' | 'Day_Mo' | 'Day_Tu' | 'Day_We' | 'Day_Th' | 'Day_Fr' | 'Day_Sa';
  value: string;
};

export const weekdayData: Weekday[] = [
  {
    label: 'Sunday',
    parameter: 'Day_Su',
    value: '1',
  },
  {
    label: 'Monday',
    parameter: 'Day_Mo',
    value: '2',
  },
  {
    label: 'Tuesday',
    parameter: 'Day_Tu',
    value: '3',
  },
  {
    label: 'Wednesday',
    parameter: 'Day_We',
    value: '4',
  },
  {
    label: 'Thursday',
    parameter: 'Day_Th',
    value: '5',
  },
  {
    label: 'Friday',
    parameter: 'Day_Fr',
    value: '6',
  },
  {
    label: 'Saturday',
    parameter: 'Day_Sa',
    value: '7',
  },
];

export type StatusType = 'active' | 'completed';

interface LeagueFiltersProps {
  filters: FilterState;
  updateFilter: <T extends FilterState, K extends keyof T>(key: K, value: T[K]) => void;
  resetFilters: () => void;
  countriesData: Country[];
}

export const LargeScreensLeagueFilters = ({
  filters,
  updateFilter,
  resetFilters,
  countriesData,
}: LeagueFiltersProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const newSearchParams = new URLSearchParams(searchParams?.toString());

  const [formatsGroupPlayersData, setFormatsGroupPlayersData] = useState(formatsData);
  const getFormatsForPlayerGroup = (playerGroupValue: string) => {
    if (playerGroupValue === '3' && formatsData[1] && formatsData[2]) {
      return setFormatsGroupPlayersData([formatsData[1], formatsData[2]]);
    } else {
      return setFormatsGroupPlayersData(formatsData);
    }
  };

  const [menuOpen, setMenuOpen] = useState({
    status: false,
    state: false,
    country: false,
    playerGroups: false,
    formats: false,
    weekDays: false,
  });

  const { data: states } = useGetLeagueStatesByCountry(filters.country.id);

  return (
    <>
      <div className='flex w-full justify-center py-2'>
        <div className='flex w-full max-w-screen-xl flex-col items-center justify-between gap-2 lg:flex-row 2xl:px-0'>
          <div className='flex w-full gap-2 lg:w-fit'>
            <span className='relative w-full lg:w-[274px]'>
              <DebounceInput
                type='text'
                className={`border-1 h-[44px] w-full !rounded-md  border !border-gray-300 bg-white !px-3 py-1.5  focus:border-gray-200 focus:outline-none focus:ring focus:ring-gray-100`}
                placeholder={`Search for a ${LEAGUE_LABELS.singular}...`}
                minLength={2}
                debounceTimeout={750}
                value={filters.keyword}
                onChange={(e) => {
                  newSearchParams.set('keyword', e.target.value);
                  router.replace(`?${newSearchParams?.toString()}`);
                  updateFilter('keyword', e.target.value);
                }}
              />
            </span>
          </div>

          <div className='flex items-center justify-between gap-2'>
            <div>
              <Menu
                placement='bottom-start'
                offset={5}
                open={menuOpen.status}
                onOpenChange={(status) => setMenuOpen({ ...menuOpen, status })}
              >
                <Menu.Trigger
                  asChild
                  onClick={() => setMenuOpen({ ...menuOpen, status: !menuOpen.status })}
                >
                  <Button
                    variant='secondary'
                    className={clsx(
                      '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !px-3 !font-normal !text-gray-500 transition-all'
                    )}
                    suffixIcon={
                      <FontAwesomeIcon icon={menuOpen.status ? faAngleUp : faAngleDown} />
                    }
                  >
                    Status
                  </Button>
                </Menu.Trigger>
                <Menu.List className='!max-h-[240px] w-fit min-w-[200px] overflow-y-scroll'>
                  <Menu.Item
                    className={clsx(
                      'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                      filters.status === 'active' ? '!bg-leagues-300 text-white' : 'text-gray-900'
                    )}
                    isActive={filters.status === 'active'}
                    onClick={() => {
                      updateFilter('status', 'active');
                    }}
                  >
                    Active
                  </Menu.Item>
                  <Menu.Item
                    className={clsx(
                      'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                      filters.status === 'completed'
                        ? '!bg-leagues-300 text-white'
                        : 'text-gray-900'
                    )}
                    isActive={filters.status === 'completed'}
                    onClick={() => {
                      updateFilter('status', 'completed');
                    }}
                  >
                    Completed
                  </Menu.Item>
                </Menu.List>
              </Menu>
            </div>

            <div>
              <Menu
                placement='bottom-start'
                offset={5}
                open={menuOpen.country}
                onOpenChange={(country) => setMenuOpen({ ...menuOpen, country })}
              >
                <Menu.Trigger
                  asChild
                  onClick={() => setMenuOpen({ ...menuOpen, country: !menuOpen.country })}
                >
                  <Button
                    variant='secondary'
                    className={clsx(
                      '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !px-3 !font-normal !text-gray-500 transition-all'
                    )}
                    suffixIcon={
                      <FontAwesomeIcon icon={menuOpen.country ? faAngleUp : faAngleDown} />
                    }
                  >
                    Country
                  </Button>
                </Menu.Trigger>
                <Menu.List className='!max-h-[240px] w-fit min-w-[200px] overflow-y-scroll'>
                  <Menu.Item
                    className={clsx(
                      'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                      !filters.country.id ? '!bg-leagues-300 text-white' : 'text-gray-900'
                    )}
                    isActive={!filters.country.id}
                    onClick={() => {
                      updateFilter('country', {
                        id: '',
                        stateId: '',
                      });
                    }}
                  >
                    All
                  </Menu.Item>
                  {countriesData.map((ct) => (
                    <Menu.Item
                      key={ct.id}
                      className={clsx(
                        'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                        filters.country.id === ct.id.toString()
                          ? '!bg-leagues-300 text-white'
                          : 'text-gray-900'
                      )}
                      isActive={filters.country.id === ct.id.toString()}
                      onClick={() => {
                        updateFilter('country', { id: ct.id.toString(), stateId: '' });
                      }}
                    >
                      {ct.title}
                    </Menu.Item>
                  ))}
                </Menu.List>
              </Menu>
            </div>

            <div>
              <Menu
                placement='bottom-start'
                offset={5}
                open={menuOpen.state}
                onOpenChange={(state) => setMenuOpen({ ...menuOpen, state })}
              >
                <Menu.Trigger
                  asChild
                  onClick={() => setMenuOpen({ ...menuOpen, state: !menuOpen.state })}
                >
                  <Button
                    variant='secondary'
                    className={clsx(
                      '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !px-3 !font-normal !text-gray-500 transition-all'
                    )}
                    suffixIcon={<FontAwesomeIcon icon={menuOpen.state ? faAngleUp : faAngleDown} />}
                  >
                    State
                  </Button>
                </Menu.Trigger>
                <Menu.List className='!max-h-[240px] w-fit min-w-[200px] overflow-y-scroll'>
                  {states.results.length === 0 ? (
                    <div className='py-3 text-center text-sm font-normal text-gray-400'>
                      Please first select country.
                    </div>
                  ) : (
                    states.results.map((st) => (
                      <Menu.Item
                        key={st.id}
                        className={clsx(
                          'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                          filters.country.stateId === st.id.toString()
                            ? '!bg-leagues-300 text-white'
                            : 'text-gray-900'
                        )}
                        isActive={filters.country.stateId === st.id.toString()}
                        onClick={() => {
                          updateFilter('country', {
                            id: filters.country.id,
                            stateId: st.id.toString(),
                          });
                        }}
                      >
                        {st.title}
                      </Menu.Item>
                    ))
                  )}
                </Menu.List>
              </Menu>
            </div>

            <div>
              <Menu
                placement='bottom-start'
                offset={5}
                open={menuOpen.playerGroups}
                onOpenChange={(playerGroups) => setMenuOpen({ ...menuOpen, playerGroups })}
              >
                <Menu.Trigger
                  asChild
                  onClick={() => setMenuOpen({ ...menuOpen, playerGroups: !menuOpen.playerGroups })}
                >
                  <Button
                    variant='secondary'
                    className={clsx(
                      '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !px-3 !font-normal !text-gray-500 transition-all'
                    )}
                    suffixIcon={
                      <FontAwesomeIcon icon={menuOpen.playerGroups ? faAngleUp : faAngleDown} />
                    }
                  >
                    Player Groups
                  </Button>
                </Menu.Trigger>
                <Menu.List className='!max-h-[240px] w-fit min-w-[200px] overflow-y-scroll'>
                  {playerGroupData.map((pg) => (
                    <Menu.Item
                      key={pg.value}
                      className={clsx(
                        'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                        filters.playerGroupsId === pg.value
                          ? '!bg-leagues-300 text-white'
                          : 'text-gray-900'
                      )}
                      isActive={filters.playerGroupsId === pg.value}
                      onClick={() => {
                        if (filters.playerGroupsId === pg.value) {
                          updateFilter('playerGroupsId', '');
                          getFormatsForPlayerGroup(pg.value);
                        } else {
                          updateFilter('playerGroupsId', pg.value);
                          getFormatsForPlayerGroup(pg.value);
                        }
                      }}
                    >
                      {pg.label}
                    </Menu.Item>
                  ))}
                </Menu.List>
              </Menu>
            </div>

            <div>
              <Menu
                placement='bottom-start'
                offset={5}
                open={menuOpen.formats}
                onOpenChange={(formats) => setMenuOpen({ ...menuOpen, formats })}
              >
                <Menu.Trigger
                  asChild
                  onClick={() => setMenuOpen({ ...menuOpen, formats: !menuOpen.formats })}
                >
                  <Button
                    variant='secondary'
                    className={clsx(
                      '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !px-3 !font-normal !text-gray-500 transition-all'
                    )}
                    suffixIcon={
                      <FontAwesomeIcon icon={menuOpen.formats ? faAngleUp : faAngleDown} />
                    }
                  >
                    Formats
                  </Button>
                </Menu.Trigger>
                <Menu.List className='!max-h-[240px] w-fit min-w-[200px] overflow-y-scroll'>
                  {formatsGroupPlayersData.map((fd) => (
                    <Menu.Item
                      key={fd.value}
                      className={clsx(
                        'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                        filters.formatsId === fd.value
                          ? '!bg-leagues-300 text-white'
                          : 'text-gray-900'
                      )}
                      isActive={filters.formatsId === fd.value}
                      onClick={() => {
                        if (filters.formatsId === fd.value) {
                          updateFilter('formatsId', '');
                        } else {
                          updateFilter('formatsId', fd.value);
                        }
                      }}
                    >
                      {fd.label}
                    </Menu.Item>
                  ))}
                </Menu.List>
              </Menu>
            </div>

            <div>
              <Menu
                placement='bottom-start'
                offset={5}
                open={menuOpen.weekDays}
                onOpenChange={(weekDays) => setMenuOpen({ ...menuOpen, weekDays })}
              >
                <Menu.Trigger
                  asChild
                  onClick={() => setMenuOpen({ ...menuOpen, weekDays: !menuOpen.weekDays })}
                >
                  <Button
                    variant='secondary'
                    className={clsx(
                      '!border-1 !h-[44px] !rounded-lg !border-gray-300 !bg-white !px-3 !font-normal !text-gray-500 transition-all'
                    )}
                    suffixIcon={
                      <FontAwesomeIcon icon={menuOpen.weekDays ? faAngleUp : faAngleDown} />
                    }
                  >
                    Day of the Week
                  </Button>
                </Menu.Trigger>
                <Menu.List className='!max-h-[240px] w-fit min-w-[200px] overflow-y-scroll'>
                  {weekdayData.map((wd) => (
                    <Menu.Item
                      key={wd.value}
                      className={clsx(
                        'mb-1 flex h-8 cursor-pointer items-center rounded-md px-3 text-sm font-normal hover:bg-gray-100',
                        filters.weekDays.parameter === wd.parameter &&
                          filters.weekDays.value === wd.value
                          ? '!bg-leagues-300 text-white'
                          : 'text-gray-900'
                      )}
                      isActive={
                        filters.weekDays.parameter === wd.parameter &&
                        filters.weekDays.value === wd.value
                      }
                      onClick={() => {
                        if (
                          filters.weekDays.parameter === wd.parameter &&
                          filters.weekDays.value === wd.value
                        ) {
                          updateFilter('weekDays', { parameter: '', value: '' });
                        } else {
                          updateFilter('weekDays', { parameter: wd.parameter, value: wd.value });
                        }
                      }}
                    >
                      {wd.label}
                    </Menu.Item>
                  ))}
                </Menu.List>
              </Menu>
            </div>
          </div>

          <div className='hidden animate-fade-in gap-2 lg:flex'>
            <Button variant='secondary' className='bg-white' onClick={resetFilters} size='lg'>
              Reset
            </Button>
            <Button
              variant='primary'
              className='!bg-leagues-300'
              onClick={() => router.replace(`?${getLeagueSearchParams(filters)?.toString()}`)}
              size='lg'
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
      <div className='container mx-auto mt-2 2xl:px-0'>
        {filters.status ? (
          <span className='mr-4 inline-flex items-center gap-1  rounded-full  bg-gray-900 px-4 py-[10px] text-sm  font-medium tracking-wide  text-white hover:bg-gray-700 '>
            {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
            <span
              className=' ml-2 cursor-pointer  text-white'
              onClick={() =>
                updateFilter('status', filters.status === 'active' ? 'completed' : 'active')
              }
            >
              <FontAwesomeIcon icon={faTimes} />
            </span>
          </span>
        ) : null}
        {filters.country.id !== '' && (
          <span className='mr-4 inline-flex items-center gap-1  rounded-full  bg-gray-900  px-4 py-[10px] text-sm  font-medium tracking-wide  text-white hover:bg-gray-700'>
            {`Country: ${countriesData.filter((c) => c.id.toString() === filters.country.id).map((c) => c.title)}`}
            <span
              className=' ml-2 cursor-pointer  text-white'
              onClick={() => {
                updateFilter('country', { id: '', stateId: '' });
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </span>
          </span>
        )}
        {filters.country.stateId !== '' && (
          <span className='mr-4 inline-flex items-center gap-1  rounded-full bg-gray-900  px-4 py-[10px] text-sm  font-medium tracking-wide  text-white hover:bg-gray-700'>
            {`State: ${states.results.filter((s) => s.id.toString() === filters.country.stateId).map((s) => s.title)}`}
            <span
              className=' ml-2 cursor-pointer  text-white'
              onClick={() => updateFilter('country', { id: filters.country.id, stateId: '' })}
            >
              <FontAwesomeIcon icon={faTimes} />
            </span>
          </span>
        )}
        {filters.playerGroupsId !== '' &&
          playerGroupData
            .filter((pg) => pg.value === filters.playerGroupsId)
            .map((pg) => {
              return (
                <span
                  key={pg.value}
                  className='mr-4 inline-flex items-center gap-1  rounded-full  bg-gray-900 px-4 py-[10px] text-sm  font-medium tracking-wide  text-white hover:bg-gray-700'
                >
                  {pg.label}
                  <span
                    className=' ml-2 cursor-pointer  text-white'
                    onClick={() => updateFilter('playerGroupsId', '')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                </span>
              );
            })}
        {filters.formatsId !== '' &&
          formatsData
            .filter((fd) => fd.value === filters.formatsId)
            .map((fd) => {
              return (
                <span
                  key={fd.value}
                  className='mr-4 inline-flex items-center gap-1  rounded-full  bg-gray-900 px-4 py-[10px] text-sm  font-medium tracking-wide  text-white hover:bg-gray-700'
                >
                  {fd.label}
                  <span
                    className=' ml-2 cursor-pointer  text-white'
                    onClick={() => updateFilter('formatsId', '')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                </span>
              );
            })}
        {filters.weekDays.parameter !== '' &&
          filters.weekDays.value !== '' &&
          weekdayData
            .filter(
              (wd) =>
                wd.parameter === filters.weekDays.parameter && wd.value === filters.weekDays.value
            )
            .map((wd) => {
              return (
                <span
                  key={wd.value}
                  className='mr-4 inline-flex items-center gap-1  rounded-full  bg-gray-900 px-4 py-[10px] text-sm  font-medium tracking-wide  text-white hover:bg-gray-700'
                >
                  {wd.label}
                  <span
                    className=' ml-2 cursor-pointer  text-white'
                    onClick={() => {
                      updateFilter('weekDays', { parameter: '', value: '' });
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                </span>
              );
            })}
      </div>
    </>
  );
};
